import type { ApprovalToolCall } from './approval';
import type { DownloadLink, StreamEvent, StreamEventMap, StreamEventName } from './streamEvent';

export interface RawStreamMessage {
  event: string;
  data: Record<string, unknown>;
}

type Normalizer<K extends StreamEventName> = (raw: Record<string, unknown>) => StreamEventMap[K];

function str(v: unknown): string {
  return (v as string) || '';
}

const normalizers: { [K in StreamEventName]: Normalizer<K> } = {
  request_received: (d) => ({
    runId: str(d.runId),
    threadId: str(d.threadId),
    elapsedMs: d.elapsedMs as number | undefined,
  }),
  run_start: (d) => ({
    runId: str(d.runId),
    threadId: str(d.threadId),
  }),
  progress: (d) => ({
    phase: str(d.phase),
    message: str(d.message),
    detail: d.detail as string | undefined,
    elapsedMs: d.elapsedMs as number | undefined,
  }),
  token: (d) => ({
    content: str(d.content),
  }),
  node_start: (d) => ({
    node: str(d.node),
    runId: str(d.runId),
  }),
  node_end: (d) => ({
    node: str(d.node),
    runId: str(d.runId),
  }),
  tool_start: (d) => ({
    toolName: str(d.toolName) || str(d.tool_name),
    displayName: d.displayName as string | undefined,
  }),
  tool_end: (d) => ({
    toolName: str(d.toolName) || str(d.tool_name),
    durationMs: d.durationMs as number | undefined,
    status: d.status as 'success' | 'error' | undefined,
  }),
  approval_required: (d) => ({
    type: 'tool_approval' as const,
    threadId: str(d.threadId) || str(d.thread_id),
    approvalId: str(d.approvalId) || str(d.approval_id),
    toolCalls: (d.toolCalls as ApprovalToolCall[]) || (d.tool_calls as ApprovalToolCall[]) || [],
    message: str(d.message),
  }),
  error: (d) => ({
    errorType: str(d.errorType) || str(d.code),
    message: str(d.message),
    retryable: (d.retryable as boolean) ?? true,
    code: d.code as string | undefined,
  }),
  heartbeat: (d) => ({
    elapsedMs: (d.elapsedMs as number) || 0,
  }),
  run_end: (d) => {
    const rawLinks = d.downloadLinks ?? d.download_links;
    return {
      threadId: str(d.threadId),
      durationMs: (d.durationMs as number) || 0,
      status: (str(d.status) || 'success') as 'success' | 'interrupted' | 'error',
      response: d.response as string | undefined,
      downloadLinks: Array.isArray(rawLinks) ? (rawLinks as DownloadLink[]) : [],
    };
  },
};

export function normalizeStreamEvent(raw: RawStreamMessage): StreamEvent {
  const name = raw.event as StreamEventName;
  const normalizer = normalizers[name];
  if (normalizer) {
    return { event: name, data: normalizer(raw.data) } as StreamEvent;
  }
  return { event: 'progress', data: { phase: 'unknown', message: '' } };
}
